/* eslint-env es6 */
/* eslint-disable no-restricted-syntax */

const filterValueDatasetProperty = "filterValue"; // data-filter-value
const hideClass = "d-none";

class Filter {
	/**
	 * @param {HTMLElement} element
	 */
	isHidden(element) {
		return !element.offsetParent;
	}

	/**
	 * @param {HTMLElement} element
	 */
	isVisible(element) {
		// I need to call the instance because we don't have an
		// option in the build system to pass the context option
		// for the module so that this is not undefined. I don't
		// even know why this is undefined because it's only
		// supposed to do that at top level, not in the scope of
		// my class. See: https://rollupjs.org/troubleshooting/#error-this-is-undefined
		return !AopsFilter.isHidden(element);
	}

	/**
	 * Hides the elements matching a filter's value.
	 * @param {HTMLElement[]} elements
	 * @param {string} filterName
	 * @param {string[]} values
	 */
	hideMatchingElements(elements, filterName, values) {
		// My string array size 1 turns into a string here but not in any of the previous steps
		if (typeof values === "string") {
			values = [values];
		}

		outer: for (const element of elements) {
			const filters = element.dataset[filterName].split(" ");

			for (const filter of filters) {
				if (!values.includes(filter)) {
					continue outer;
				}
			}

			element.classList.add(hideClass);
		}
	}

	/**
	 * Run a filter on checkboxes that might have multiple checkboxes with the
	 * same value. This works even if there is only one checkbox per filter
	 * value and still costs O(n^2).
	 * @param {HTMLInputElement[]} checkboxes
	 * @param {HTMLInputElement[]} elements
	 * @param {string} filter
	 */
	filterGrouped(checkboxes, elements, filter) {
		let [groups, length] = groupFilters(checkboxes);
		// IF none OR all filters are off, THEN do nothing
		if (groups.length < 1 || groups.length >= length) {
			return;
		}

		this.hideMatchingElements(elements, filter, groups);
	}

	/**
	 * Checks a checkbox or radio.
	 * @param {HTMLInputElement} input
	 * @param state True for checked, false otherwise
	 */
	check(input, state = true) {
		input.checked = !state;
		input.click();
	}

	/**
	 * Unchecks a checkbox or radio.
	 * @param {HTMLInputElement} input
	 */
	uncheck(input) {
		this.check(input, false);
	}
}

/**
 * Group checkboxes by their filter value and keep the groups that are
 * all unchecked.
 * @param {HTMLInputElement[]} checkboxes
 * @returns {[string[],number]} The values with active filters and the
 * 	total number of groups
 */
function groupFilters(checkboxes) {
	let groups = groupCheckboxes(checkboxes);
	const size = groups.size;

	groups = filterEmptyGroups(groups);

	return [groups, size];
}

/**
 * Filter the groups to keep the ones with all of their checkboxes
 * unchecked.
 * @param {Map<string,HTMLInputElement>} groups
 */
function filterEmptyGroups(groups) {
	const totallyUncheckedGroups = [];

	for (const [value, group] of groups.entries()) {
		if (group.every((cb) => !cb.checked)) {
			totallyUncheckedGroups.push(value);
		}
	}

	return totallyUncheckedGroups;
}

/**
 * Group checkboxes by their value because multiple checkboxes may
 * control the same filter value.
 * @param {HTMLInputElement[]} checkboxes
 * @returns {Map<string,HTMLInputElement>} A map with the sorted checkboxes
 */
function groupCheckboxes(checkboxes) {
	const groups = new Map();

	for (const checkbox of checkboxes) {
		const value = checkbox.dataset[filterValueDatasetProperty];

		if (groups.has(value)) {
			groups.get(value).push(checkbox);
		} else {
			groups.set(value, [checkbox]);
		}
	}

	return groups;
}

export const AopsFilter = new Filter();
